<script setup lang="ts">
// components
import { ComboboxOption, ComboboxOptions } from "@headlessui/vue";
import type { EventDto } from "mws-catalog-ts-sdk";
import type { AdminCustomerResponse } from "mws-finance-ts-sdk";

import { isAdminCustomerDto } from "@/customers/public/utils/isAdminCustomerDto";
import { isEventDto } from "@/events/utils/isEventDto";
import { type AdminProductSku, ProductSku } from "@/products/types";

import type { ItemGroup } from "./CommandPalette.vue";
import EventListItem from "./EventListItem.vue";
import ProductListingListItem from "./ProductListingListItem.vue";
// types and classes

withDefaults(
  defineProps<{
    groups: {
      productListings: ItemGroup<AdminProductSku>;
      events: ItemGroup<EventDto>;
      customers: ItemGroup<AdminCustomerResponse>;
    };
    searchTerm?: string;
  }>(),
  {
    searchTerm: "",
  },
);
</script>

<template>
  <ComboboxOptions
    :static="true"
    class="results-list"
  >
    <li
      v-for="(group, index) in groups"
      :key="index"
    >
      <div v-if="group.items.length">
        <h2 class="results-list__group-title">
          <span>{{ group.label }}</span>
          <RouterLink
            :to="{ path: group.seeAllLink, query: { searchTerm } }"
            class="results-list__see-all-link"
          >
            See all
          </RouterLink>
        </h2>
        <ul class="results-list__list">
          <ComboboxOption
            v-for="item in group.items"
            :key="item.id"
            v-slot="{ active }"
            :value="item"
            as="template"
          >
            <li
              class="results-list__item"
              :class="[active && 'results-list__item--active']"
            >
              <ProductListingListItem
                v-if="item instanceof ProductSku"
                :product-listing="item"
                :is-active="active"
              />
              <EventListItem
                v-if="isEventDto(item)"
                :event="item"
                :is-active="active"
              />
              <div
                v-else-if="isAdminCustomerDto(item)"
                class="results-list__item__user"
              >
                {{ item.emailAddress }}
              </div>
            </li>
          </ComboboxOption>
        </ul>
      </div>
    </li>
  </ComboboxOptions>
</template>

<style scoped lang="postcss">
.results-list {
  @apply scroll-pt-11 max-h-96 scroll-pb-2 space-y-2 overflow-y-auto pb-2;

  &__group-title {
    @apply bg-gray-100 py-2.5 px-4 text-xs font-semibold text-gray-900  flex justify-between w-full;
  }

  &__see-all-link {
    @apply text-indigo-400 hover:text-indigo-600;
  }

  &__list {
    @apply mt-2 text-sm text-gray-800;
  }

  &__item {
    @apply cursor-pointer select-none px-4 py-2;
    &--active {
      @apply bg-indigo-600 text-white;
    }

    &__user {
      @apply py-2;
    }
  }
}
</style>
